
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Container, Grid, Paper, TextField, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import TextfieldCustom from '../../../common/TextfieldCustom';
import { fetchCoursesById, updateCourseById, updateCourseImage, fetchProgramCourseImageById, fetchProgramList } from '../../../redux/actions/Actions';
import axiosinstance from '../../../../api/axiosinstance';
import securedInstance from '../../../../api/securedInstance';
import { toast } from 'react-toastify';

const DEFAULTS = {
  hero_pills: ['3 Months', '6 Months', 'Offline', 'Online', 'Hybrid'],
  hero_checks: ['Industry Mentors', 'Live Project Internship', 'Hands-on Training', 'Mini Projects', 'Capstone Project', 'Internship Certificate'],
  info_bar: [
    { title: 'PROGRAM MODE', text: 'Online | Offline | Hybrid' },
    { title: 'ELIGIBILITY', text: 'Any Graduate / Final Year Students' },
    { title: 'BATCH SIZE', text: 'Small Batches for Individual Attention' },
    { title: 'CERTIFICATE', text: 'UPL Completion Certificate' },
    { title: 'TOOLS & LMS', text: 'Industry Standard Tools & LMS Access' },
    { title: 'MOCK TESTS & INTERVIEWS', text: 'Regular Assessments, Mocks & Feedback' },
  ],
  learn_topics: [
    { title: 'Core Java', color: '#EF4444', items: ['Java Basics', 'OOPs Concept', 'Collections', 'Exception Handling', 'Multithreading', 'Java 8 Features'] },
    { title: 'Web Technologies', color: '#F59E0B', items: ['HTML5', 'CSS3', 'JavaScript ES6', 'Bootstrap 5', 'jQuery'] },
    { title: 'Frontend Framework', color: '#10B981', items: ['React JS', 'Components', 'Props & State', 'React Router', 'Hooks', 'Redux (Basics)'] },
    { title: 'Backend Development', color: '#3B82F6', items: ['Servlets & JSP', 'RESTful Web Services', 'Spring Core', 'Spring MVC', 'Spring Boot', 'Spring Security'] },
    { title: 'Database', color: '#8B5CF6', items: ['MySQL', 'JDBC', 'Hibernate / JPA', 'SQL Queries', 'Database Design'] },
    { title: 'Tools & Others', color: '#EC4899', items: ['Git & GitHub', 'Maven', 'Postman', 'Swagger', 'Docker (Basics)', 'JIRA, Logging'] },
    { title: 'Real World & DevOps', color: '#06B6D4', items: ['AWS Basics', 'CI/CD Pipeline', 'Deployment', 'Agile / Scrum', 'Performance Optimization'] },
  ],
  technologies: ['Java', 'Spring Boot', 'Hibernate', 'MySQL', 'HTML5', 'CSS3', 'JavaScript', 'React.js', 'Git & GitHub', 'Postman'],
  live_projects: ['E-Commerce Web Application', 'Online Banking System', 'Employee Management System', 'Hospital Management System', 'Social Media Application', 'Task Management System'],
  highlights: ['Hands-on Live Projects', 'Real-time Industry Use Cases', 'Code Reviews & Mentorship', 'Agile & Scrum Methodology', 'Resume & LinkedIn Profile Building'],
  stats: [
    { value: '10,000+', label: 'Students Trained' },
    { value: '250+', label: 'Hiring Partners' },
    { value: '500+', label: 'Live Projects' },
    { value: '95%+', label: 'Placement Rate' },
  ],
};

const sectionStyle = { border: '1px solid #e2e8f0', borderRadius: 10, margin: '12px 0', overflow: 'hidden' };
const summaryStyle = { padding: '12px 16px', fontWeight: 700, fontSize: 14, color: '#1E3A8A', cursor: 'pointer', background: '#F1F5F9', userSelect: 'none' };
const innerStyle = { padding: '14px 16px', background: '#fff' };

const ListEditor = ({ label, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 10 }}>
    {label && <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 12, color: '#475569' }}>{label}</p>}
    <textarea
      rows={4}
      style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
      value={(value || []).join('\n')}
      onChange={e => onChange(e.target.value.split('\n'))}
      placeholder={placeholder || 'One item per line'}
    />
  </div>
);

const UpdateCourse = () => {
  const fetchCourseById = useSelector((state) => state.fetchCourseById.courseById);
  const programCourseFilesById = useSelector((state) => state.fetchProgramCourseFiles.programCourseFilesById);
  const programs = useSelector((state) => state.fetchAllPrograms.programs) || [];
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const [Image, setImage] = useState(null);
  const [Pdf, setPdf] = useState(null);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const programsFetchedRef = useRef(false);

  const selectedCourse = fetchCourseById[0];
  const currentCourseFiles = programCourseFilesById[0];

  const [formData, setFormData] = useState({ course_name: '', description: '', duration: '' });
  const [details, setDetails] = useState(JSON.parse(JSON.stringify(DEFAULTS)));

  const { course_name, description, duration } = formData;

  useEffect(() => {
    dispatch(fetchCoursesById(id));
    dispatch(fetchProgramList());
    axiosinstance.get(`/api/category/course-programs/${id}`)
      .then(res => { if (Array.isArray(res.data)) { programsFetchedRef.current = true; setSelectedPrograms([...new Set(res.data.map(Number))]); } })
      .catch(() => {});
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedCourse?.program_id) dispatch(fetchProgramCourseImageById(selectedCourse.program_id, id));
  }, [dispatch, id, selectedCourse?.program_id]);

  useEffect(() => {
    if (selectedCourse) {
      setFormData({ course_name: selectedCourse.course_name || '', description: selectedCourse.description || '', duration: selectedCourse.duration || '' });
      if (!programsFetchedRef.current && selectedCourse.program_id) {
        setSelectedPrograms(prev => { const pid = Number(selectedCourse.program_id); return prev.includes(pid) ? prev : [...prev, pid]; });
      }
      if (selectedCourse.course_details) {
        try {
          const saved = typeof selectedCourse.course_details === 'string' ? JSON.parse(selectedCourse.course_details) : selectedCourse.course_details;
          setDetails(prev => ({ ...JSON.parse(JSON.stringify(DEFAULTS)), ...saved }));
        } catch { /* use defaults */ }
      }
    }
  }, [selectedCourse]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleProgramToggle = (pid) => setSelectedPrograms(prev => prev.includes(pid) ? prev.filter(p => p !== pid) : [...prev, pid]);
  const updateDetails = (key, val) => setDetails(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanDetails = { ...details };
      Object.keys(cleanDetails).forEach(k => {
        if (Array.isArray(cleanDetails[k])) cleanDetails[k] = cleanDetails[k].filter(v => typeof v === 'string' ? v.trim() : true);
      });

      await dispatch(updateCourseById({
        course_name: formData.course_name,
        instructor_id: null,
        description: formData.description,
        duration: formData.duration,
        program_id: selectedPrograms[0] || selectedCourse?.program_id,
        course_details: JSON.stringify(cleanDetails),
      }, id));

      try {
        await securedInstance.put(`/api/category/course-programs/${id}`, { programIds: selectedPrograms });
        toast.success('Course updated successfully');
      } catch (err) {
        toast.error(`Program assignment failed`);
      }

      if (Image || Pdf) {
        const fd = new FormData();
        fd.append('course_id', id);
        fd.append('program_id', selectedPrograms[0] || selectedCourse?.program_id);
        if (Image) fd.append('course_image', Image);
        if (Pdf) fd.append('course_pdf', Pdf);
        dispatch(updateCourseImage(fd, id));
      }

      navigate('/auth/allCourses');
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <Container maxWidth="md" className="d-flex">
          <Paper elevation={2} sx={{ padding: 4, mt: 4, mb: 4 }}>
            <h3 style={{ margin: '0 0 4px' }}>Update Course</h3>
            <h6 style={{ margin: '0 0 16px', color: '#64748B' }}>Editing: {selectedCourse?.course_name}</h6>
            <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>

            {/* ═══ BASIC INFO ═══ */}
            <details open style={sectionStyle}>
              <summary style={summaryStyle}>Basic Course Info</summary>
              <div style={innerStyle}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><TextfieldCustom type="text" name="course_name" value={course_name} onChange={handleChange} placeholder="Course Name" required /></Grid>
                  <Grid item xs={6}><TextfieldCustom type="text" name="description" value={description} onChange={handleChange} placeholder="Description" required /></Grid>
                  <Grid item xs={6}><TextfieldCustom type="text" name="duration" value={duration} onChange={handleChange} placeholder="Duration" required /></Grid>
                </Grid>
              </div>
            </details>

            {/* ═══ PROGRAMS ═══ */}
            <details open style={sectionStyle}>
              <summary style={summaryStyle}>Assign to Programs</summary>
              <div style={innerStyle}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {programs.map(p => {
                    const checked = selectedPrograms.includes(Number(p.program_id));
                    return (
                      <label key={p.program_id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 16px', borderRadius: 20, border: `2px solid ${checked ? '#3251A3' : '#d1d5db'}`, background: checked ? '#EEF2FF' : '#fff', fontWeight: checked ? 700 : 400, fontSize: 13, color: checked ? '#3251A3' : '#374151', transition: 'all 0.15s' }}>
                        <input type="checkbox" checked={checked} onChange={() => handleProgramToggle(Number(p.program_id))} style={{ display: 'none' }} />
                        {checked && <span style={{ fontSize: 16 }}>✓</span>}
                        {p.program_name}
                      </label>
                    );
                  })}
                </div>
                {selectedPrograms.length === 0 && <p style={{ color: '#ef4444', fontSize: 12, margin: '8px 0 0' }}>Please select at least one program</p>}
              </div>
            </details>

            {/* ═══ FILES ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>Course Image & Brochure</summary>
              <div style={innerStyle}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>Course Image</p>
                    <input type="file" accept=".png,.jpg,.jpeg" onChange={e => setImage(e.target.files[0])} />
                    {currentCourseFiles?.img_Path && <a href={currentCourseFiles.img_Path} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>View current</a>}
                  </Grid>
                  <Grid item xs={6}>
                    <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>Brochure (PDF)</p>
                    <input type="file" accept=".doc,.docx,.pdf" onChange={e => setPdf(e.target.files[0])} />
                    {currentCourseFiles?.pdf_file_path && <a href={currentCourseFiles.pdf_file_path} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>View current</a>}
                  </Grid>
                </Grid>
              </div>
            </details>

            {/* ═══ HERO SECTION ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>Hero Section — Pills & Features</summary>
              <div style={innerStyle}>
                <ListEditor label="Duration / Mode Pills (one per line)" value={details.hero_pills} onChange={v => updateDetails('hero_pills', v)} placeholder="3 Months&#10;6 Months&#10;Offline&#10;Online&#10;Hybrid" />
                <ListEditor label="Feature Checklist (one per line)" value={details.hero_checks} onChange={v => updateDetails('hero_checks', v)} placeholder="Industry Mentors&#10;Live Project Internship" />
              </div>
            </details>

            {/* ═══ INFO BAR ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>Info Bar (6 items below hero)</summary>
              <div style={innerStyle}>
                {(details.info_bar || []).map((item, i) => (
                  <Grid container spacing={1} key={i} sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                      <TextField size="small" fullWidth label="Title" value={item.title} onChange={e => { const arr = [...details.info_bar]; arr[i] = { ...arr[i], title: e.target.value }; updateDetails('info_bar', arr); }} />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField size="small" fullWidth label="Description" value={item.text} onChange={e => { const arr = [...details.info_bar]; arr[i] = { ...arr[i], text: e.target.value }; updateDetails('info_bar', arr); }} />
                    </Grid>
                  </Grid>
                ))}
              </div>
            </details>

            {/* ═══ WHAT YOU WILL LEARN ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>What You Will Learn (Topic Cards)</summary>
              <div style={innerStyle}>
                {(details.learn_topics || []).map((topic, i) => (
                  <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 10, background: '#fafbfc' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <TextField size="small" label="Topic Title" value={topic.title} sx={{ flex: 1 }} onChange={e => { const arr = [...details.learn_topics]; arr[i] = { ...arr[i], title: e.target.value }; updateDetails('learn_topics', arr); }} />
                      <input type="color" value={topic.color || '#3B82F6'} style={{ width: 36, height: 36, border: 'none', cursor: 'pointer' }} onChange={e => { const arr = [...details.learn_topics]; arr[i] = { ...arr[i], color: e.target.value }; updateDetails('learn_topics', arr); }} />
                      <IconButton size="small" color="error" onClick={() => updateDetails('learn_topics', details.learn_topics.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
                    </div>
                    <textarea
                      rows={3} placeholder="One item per line"
                      style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                      value={(topic.items || []).join('\n')}
                      onChange={e => { const arr = [...details.learn_topics]; arr[i] = { ...arr[i], items: e.target.value.split('\n') }; updateDetails('learn_topics', arr); }}
                    />
                  </div>
                ))}
                <Button size="small" startIcon={<Add />} onClick={() => updateDetails('learn_topics', [...(details.learn_topics || []), { title: '', color: '#3B82F6', items: [] }])}>Add Topic</Button>
              </div>
            </details>

            {/* ═══ TECHNOLOGIES ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>Technologies Strip</summary>
              <div style={innerStyle}>
                <ListEditor value={details.technologies} onChange={v => updateDetails('technologies', v)} placeholder="Java&#10;Spring Boot&#10;React.js" />
              </div>
            </details>

            {/* ═══ LIVE PROJECTS ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>Live Projects List</summary>
              <div style={innerStyle}>
                <ListEditor value={details.live_projects} onChange={v => updateDetails('live_projects', v)} placeholder="E-Commerce Web Application&#10;Online Banking System" />
              </div>
            </details>

            {/* ═══ HIGHLIGHTS ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>Program Highlights</summary>
              <div style={innerStyle}>
                <ListEditor value={details.highlights} onChange={v => updateDetails('highlights', v)} placeholder="Hands-on Live Projects&#10;Code Reviews & Mentorship" />
              </div>
            </details>

            {/* ═══ STATS ═══ */}
            <details style={sectionStyle}>
              <summary style={summaryStyle}>Stats Bar (numbers shown at bottom)</summary>
              <div style={innerStyle}>
                {(details.stats || []).map((s, i) => (
                  <Grid container spacing={1} key={i} sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                      <TextField size="small" fullWidth label="Value" value={s.value} onChange={e => { const arr = [...details.stats]; arr[i] = { ...arr[i], value: e.target.value }; updateDetails('stats', arr); }} />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField size="small" fullWidth label="Label" value={s.label} onChange={e => { const arr = [...details.stats]; arr[i] = { ...arr[i], label: e.target.value }; updateDetails('stats', arr); }} />
                    </Grid>
                  </Grid>
                ))}
              </div>
            </details>

            {/* ═══ SUBMIT ═══ */}
            <Button variant="contained" fullWidth type="submit" disabled={selectedPrograms.length === 0} sx={{ mt: 2, py: 1.5, fontWeight: 700, fontSize: 15 }} style={{ backgroundColor: '#3251A3' }}>
              Update & Save Course
            </Button>
          </Paper>
        </Container>
      </form>
    </div>
  );
};

export default UpdateCourse;
